import cx from 'classnames'
import { FC, useId } from 'react'
import FormikField from 'src/form/FormikField'
import { FormikFormProps } from 'src/form/FormikForm'
import { separatorAnimation, textAnimation } from 'src/hooks/useAppear'
import { colors } from 'src/theme'
import { MotionWrapper } from 'src/theme/styled'

import { Icons } from '../../Icon'
import { LinkProps } from '../../Link'
import { HeroProps } from '../types'

import * as SC from './styled'

export type HeroSearchProps = HeroProps & {
  linkProps?: LinkProps
  formikFormProps?: FormikFormProps<SearchFormProps>
  search?: {
    placeholder?: string
    submitLabel?: string
    resetLabel?: string
  }
}

const HeroSearch: FC<HeroSearchProps> = ({
  className,
  htmlTag = 'hgroup',
  htmlTitleTag = 'h1',
  title,
  color = colors.blueLight,
  linkProps,
  formikFormProps,
  search,
}) => {
  const id = useId()
  const { animations } = useHeroSearchMotion()

  return (
    <SC.Container
      className={cx('HeroSearch', className)}
      as={htmlTag}
      $color={color}
    >
      {title && (
        <SC.Title as={htmlTitleTag}>
          <MotionWrapper {...animations.title}>{title}</MotionWrapper>
        </SC.Title>
      )}
      <SC._Separator aria-hidden role="separator" {...animations.separator} />
      <SC.Content>
        {formikFormProps && (
          <SC.FormikFormWrapper>
            <SC._MotionWrapper {...animations.form}>
              <SC._FormikForm {...formikFormProps}>
                {({ resetForm }) => (
                  <>
                    <FormikField
                      Component={SC.Input}
                      id={`${id}-input`}
                      name="term"
                      type="search"
                      placeholder={search?.placeholder}
                    />
                    {search?.placeholder && (
                      <SC.Placeholder htmlFor={`${id}-input`}>
                        {search.placeholder}
                      </SC.Placeholder>
                    )}
                    <SC.Reset
                      type="reset"
                      aria-label={search?.resetLabel}
                      onClick={() => resetForm({ values: { term: '' } })}
                    >
                      <SC.ResetIcon icon={Icons.closeSmall} aria-hidden />
                    </SC.Reset>
                    <SC.Submit type="submit" aria-label={search?.submitLabel}>
                      <SC.SubmitIcon icon={Icons.search} aria-hidden />
                    </SC.Submit>
                  </>
                )}
              </SC._FormikForm>
            </SC._MotionWrapper>
          </SC.FormikFormWrapper>
        )}
        {linkProps && (
          <SC.BackLinkMask>
            <MotionWrapper {...animations.link}>
              <SC.BackLink {...linkProps}>
                {linkProps?.label ?? linkProps?.text}
                <SC.BackIcon icon={Icons.back} aria-hidden />
              </SC.BackLink>
            </MotionWrapper>
          </SC.BackLinkMask>
        )}
      </SC.Content>
    </SC.Container>
  )
}

const useHeroSearchMotion = () => ({
  animations: {
    separator: separatorAnimation({ delay: 0.5, duration: 1 }),
    title: textAnimation({ delay: 0.8 }),
    form: textAnimation({ delay: 0.92 }),
    link: textAnimation({ delay: 1.04 }),
  },
})

export default HeroSearch
